/*
 * TEABALLS Promotion Center — runtime engine
 *
 * v1.3.0 (Black Month 2026)
 * - <html data-tb-identity-surfaces> mirrors the visible identity surfaces (incl. new 'accents').
 * - Campaign identity layer, independent of offers and of the Promotion Center master
 *   switch (fragment "identity", configured in Theme settings › Black Month):
 *   state.identity, identityEnabled(surface), <html data-tb-identity / data-tb-identity-phase>,
 *   scoped colour tokens --tb-identity-* on <html> only while identity is live.
 *   DOM contract: [data-tb-identity-surface="x"] gets `hidden` toggled,
 *   [data-tb-identity-host="x"] gets `data-tb-identity-active` toggled,
 *   <html data-tb-identity-annbar> while the announcement surface is live.
 * - Timing: boundary-aware ticking for campaign AND identity regardless of the current
 *   source (fixes OFF → LIVE without reload), re-evaluation on tab resume / bfcache
 *   restore, server-clock sync when the rendered timestamp looks stale (cached HTML).
 * - Eligibility: complete allowlists (paginate by 250) with a `complete` flag; page-2+
 *   listings are completed via the Section Rendering API. Unknown / incomplete /
 *   inherited eligibility never produces a campaign saving claim (status "held").
 *
 * Public API (unchanged, plus identity helpers): window.TeaballsPromotion
 * Events (unchanged): teaballs:promotion:ready | :change | :tick
 */
(() => {
  const FLAG = '__TEABALLS_PROMOTION_CENTER_LOADED__';
  if (window[FLAG] && window.TeaballsPromotion?.refresh) {
    window.TeaballsPromotion.refresh();
    return;
  }
  const EVENT_READY = 'teaballs:promotion:ready';
  const EVENT_CHANGE = 'teaballs:promotion:change';
  const EVENT_TICK = 'teaballs:promotion:tick';
  const CLOCK_TRUST_MS = 60 * 1000;
  const TICK_MAX_MS = 60 * 1000;
  const TICK_MIN_MS = 250;
  const PAGINATE_MAX = 250;
  const IDENTITY_TOKENS = ['dark', 'ivory', 'lime', 'green'];
  const IDENTITY_SURFACES = ['announcement', 'logo', 'navigation', 'homepage', 'hub', 'productCards', 'pdp', 'cart', 'signup', 'accents'];
  const PRICE_OFFER_TYPES = new Set(['flat_percent', 'fixed_amount', 'tiered', 'bxgy']);

  const listeners = new Set();
  const completedLists = new Map();
  const pendingLists = new Set();
  let rawConfig = null;
  let resolvedState = null;
  let clockOffsetMs = 0;
  let clockSource = 'client';
  let clockSyncStarted = false;
  let tickTimer = 0;

  const asNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const parseDate = (value) => {
    if (!value) return null;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const nowMs = () => Date.now() + clockOffsetMs;

  const sortTiers = (tiers = []) => [...tiers]
    .map((tier) => ({
      ...tier,
      minQuantity: Math.max(1, asNumber(tier.minQuantity, 1)),
      discountPercent: Math.max(0, asNumber(tier.discountPercent, 0)),
    }))
    .sort((a, b) => a.minQuantity - b.minQuantity);

  const sortGoals = (goals = []) => [...goals]
    .map((goal) => ({ ...goal, quantity: Math.max(1, asNumber(goal.quantity, 1)) }))
    .sort((a, b) => a.quantity - b.quantity);

  const collectionIds = (collection) => new Set((collection?.productIds || []).map((id) => String(id)));

  /* ---------- eligibility ---------- */

  const eligibilityKnown = (collection) => Boolean(collection?.id && collection.complete === true && !collection.inherited);

  const listCanBeCompleted = (collection) => Boolean(
    collection?.id
    && collection.complete !== true
    && asNumber(collection.productCount, 0) <= PAGINATE_MAX
  );

  const listKey = (fragment, collection) => `${fragment}:${collection?.id}`;

  const applyCompletedList = (fragment, collection) => {
    if (!collection?.id || collection.complete === true) return collection;
    const cached = completedLists.get(listKey(fragment, collection));
    return cached ? { ...collection, ...cached } : collection;
  };

  const hasPriceOffer = (campaign) => PRICE_OFFER_TYPES.has(campaign?.primaryOfferType);

  /* ---------- schedule ---------- */

  const thresholdMet = (offer, metrics) => {
    if (!offer) return false;
    if (offer.thresholdType === 'quantity') return metrics.eligibleQuantity >= asNumber(offer.thresholdQuantity, 0);
    if (offer.thresholdType === 'spend') return metrics.eligibleSubtotalCents >= asNumber(offer.thresholdSpendCents, 0);
    return true;
  };

  const commercialMechanicRequired = (config, source) => {
    if (source === 'campaign') {
      const campaign = config.campaign || {};
      const hasPrimary = campaign.primaryOfferType && campaign.primaryOfferType !== 'none';
      const hasGift = (config.gifts || []).some((gift) => gift.activeIn === 'campaign' || gift.activeIn === 'both');
      const hasShippingOverride = campaign.shipping?.mode === 'free' || campaign.shipping?.mode === 'threshold';
      return hasPrimary || hasGift || hasShippingOverride;
    }
    const hasEvergreenTier = Boolean(config.evergreen?.enabled && sortTiers(config.evergreen?.tiers).length);
    const hasEvergreenGift = (config.gifts || []).some((gift) => gift.activeIn === 'evergreen' || gift.activeIn === 'both');
    return hasEvergreenTier || hasEvergreenGift;
  };

  const commercialConfirmed = (config, source) => {
    const sourceConfig = source === 'campaign' ? config.campaign : config.evergreen;
    if (!commercialMechanicRequired(config, source)) return true;
    return sourceConfig?.pricingConfirmed === true;
  };

  const windowStatus = (enabled, startAt, endAt, at) => {
    if (!enabled) return { configured: false, inWindow: false, phase: 'disabled', startMs: null, endMs: null };
    const startMs = parseDate(startAt);
    const endMs = parseDate(endAt);
    if (startAt && startMs === null) return { configured: true, inWindow: false, phase: 'invalid_start', startMs, endMs };
    if (endAt && endMs === null) return { configured: true, inWindow: false, phase: 'invalid_end', startMs, endMs };
    if (startMs !== null && endMs !== null && endMs <= startMs) return { configured: true, inWindow: false, phase: 'invalid_end', startMs, endMs };
    if (startMs !== null && at < startMs) return { configured: true, inWindow: false, phase: 'scheduled', startMs, endMs };
    if (endMs !== null && at >= endMs) return { configured: true, inWindow: false, phase: 'ended', startMs, endMs };
    return { configured: true, inWindow: true, phase: 'active', startMs, endMs };
  };

  const scheduleStatus = (campaign, at = nowMs()) => windowStatus(campaign?.enabled, campaign?.startAt, campaign?.endAt, at);

  /* ---------- offer helpers ---------- */

  const campaignOffer = (campaign) => ({
    type: campaign.primaryOfferType,
    flatPercent: Math.max(0, asNumber(campaign.flatPercent, 0)),
    fixedAmountCents: Math.max(0, asNumber(campaign.fixedAmountCents, 0)),
    thresholdType: campaign.thresholdType,
    thresholdQuantity: Math.max(0, asNumber(campaign.thresholdQuantity, 0)),
    thresholdSpendCents: Math.max(0, asNumber(campaign.thresholdSpendCents, 0)),
    tiers: sortTiers(campaign.tiers),
    bxgy: campaign.bxgy || {},
  });

  const evergreenOffer = (evergreen) => ({
    type: 'tiered',
    flatPercent: 0,
    fixedAmountCents: 0,
    thresholdType: 'none',
    thresholdQuantity: 0,
    thresholdSpendCents: 0,
    tiers: sortTiers(evergreen.tiers),
    bxgy: {},
  });

  const effectiveShipping = (config, source) => {
    if (source === 'campaign') {
      const mode = config.campaign.shipping?.mode || 'inherit';
      if (mode === 'free') return { mode: 'free', thresholdCents: 0 };
      if (mode === 'threshold') return { mode: 'threshold', thresholdCents: Math.max(0, asNumber(config.campaign.shipping?.thresholdCents, 0)) };
    }
    const thresholdCents = Math.max(0, asNumber(config.evergreen.shippingThresholdCents, 0));
    return { mode: thresholdCents > 0 ? 'threshold' : 'none', thresholdCents };
  };

  const activeGifts = (config, source) => (config.gifts || []).filter((gift) => {
    if (gift.activeIn === 'both') return true;
    return gift.activeIn === source;
  });

  /* ---------- identity ---------- */

  const offSurfaces = () => Object.fromEntries(IDENTITY_SURFACES.map((name) => [name, false]));

  const resolveIdentity = (config, at = nowMs()) => {
    const identity = config.identity;
    if (!identity) {
      return { configured: false, key: '', phase: 'missing', reason: 'identity_fragment_missing', visible: false, startMs: null, endMs: null, surfaces: offSurfaces(), tokens: {}, assets: {}, publicName: '', hubUrl: '', alt: '' };
    }
    const span = windowStatus(identity.enabled, identity.startAt, identity.endAt, at);
    const editorPreview = Boolean(config.designMode && identity.previewInEditor && identity.enabled);
    const themeRole = window.Shopify?.theme?.role || '';
    const unpublishedPreview = Boolean(identity.enabled && identity.previewUnpublished && themeRole && themeRole !== 'main');
    let phase = span.phase === 'active' ? 'live' : span.phase;
    let reason = `identity_${phase}`;
    if (editorPreview) {
      phase = 'live';
      reason = 'theme_editor_preview';
    } else if (unpublishedPreview) {
      phase = 'live';
      reason = 'unpublished_theme_preview';
    }
    const visible = phase === 'live';
    const surfaces = offSurfaces();
    if (visible) for (const name of IDENTITY_SURFACES) surfaces[name] = Boolean(identity.surfaces?.[name]);
    return {
      configured: true,
      key: identity.key || '',
      publicName: identity.publicName || '',
      phase,
      reason,
      visible,
      startMs: span.startMs,
      endMs: span.endMs,
      hubUrl: identity.hubUrl || '',
      alt: identity.alt || identity.publicName || '',
      assets: identity.assets || {},
      tokens: identity.tokens || {},
      surfaces,
    };
  };

  const applyIdentityToDocument = (identity) => {
    const root = document.documentElement;
    if (!root) return;
    if (identity?.visible && identity.key) {
      root.dataset.tbIdentity = identity.key;
      for (const token of IDENTITY_TOKENS) {
        const value = identity.tokens?.[token];
        if (value) root.style.setProperty(`--tb-identity-${token}`, value);
        else root.style.removeProperty(`--tb-identity-${token}`);
      }
    } else {
      delete root.dataset.tbIdentity;
      for (const token of IDENTITY_TOKENS) root.style.removeProperty(`--tb-identity-${token}`);
    }
    if (identity?.configured) root.dataset.tbIdentityPhase = identity.phase;
    else delete root.dataset.tbIdentityPhase;
    const surfaceOn = (name) => Boolean(identity?.visible && identity.surfaces?.[name]);
    if (surfaceOn('announcement')) root.dataset.tbIdentityAnnbar = '';
    else delete root.dataset.tbIdentityAnnbar;
    /* mirrors the server-rendered attribute (snippets/teaballs-bm-html-attrs.liquid) */
    const visibleSurfaces = IDENTITY_SURFACES.filter(surfaceOn).join(' ');
    if (visibleSurfaces) {
      if (root.dataset.tbIdentitySurfaces !== visibleSurfaces) root.dataset.tbIdentitySurfaces = visibleSurfaces;
    } else {
      delete root.dataset.tbIdentitySurfaces;
    }
    for (const element of document.querySelectorAll('[data-tb-identity-surface]')) {
      const hide = !surfaceOn(element.dataset.tbIdentitySurface);
      if (element.hidden !== hide) element.hidden = hide;
    }
    for (const element of document.querySelectorAll('[data-tb-identity-host]')) {
      const on = surfaceOn(element.dataset.tbIdentityHost);
      if (on !== element.hasAttribute('data-tb-identity-active')) element.toggleAttribute('data-tb-identity-active', on);
    }
    for (const output of document.querySelectorAll('[data-tb-identity-admin-phase]')) {
      output.textContent = `${identity?.phase || 'missing'} (${identity?.reason || '—'}) · clock: ${clockSource}`;
    }
  };

  /* ---------- diagnostics ---------- */

  const eligibilityDiagnostics = (issues, label, collection, required) => {
    if (!collection?.id) return;
    const count = asNumber(collection.productCount, 0);
    if (count > PAGINATE_MAX) {
      issues.push({ level: 'error', code: `${label}_collection_over_${PAGINATE_MAX}`, message: `${label} eligible collection has ${count} products; storefront allowlists support at most ${PAGINATE_MAX}. Eligibility is unknown — saving claims are held.` });
    } else if (collection.complete !== true) {
      issues.push({ level: required ? 'error' : 'warning', code: `${label}_collection_incomplete`, message: `${label} eligibility list is incomplete (${asNumber(collection.listedCount, 0)}/${count}); completing via Section Rendering API.` });
    }
  };

  const diagnosticsFor = (config, state, schedule) => {
    const issues = [];
    if (!config.master?.enabled) issues.push({ level: 'info', code: 'system_off', message: 'Promotion Center offers are disabled (campaign identity is controlled separately in Theme settings).' });
    if (config.evergreen?.enabled && !config.evergreen?.eligibleCollection?.id) issues.push({ level: 'warning', code: 'evergreen_collection_missing', message: 'Evergreen eligible collection is not configured.' });
    eligibilityDiagnostics(issues, 'evergreen', config.evergreen?.eligibleCollection, false);
    if (config.campaign?.enabled && hasPriceOffer(config.campaign)) {
      if (!config.campaign?.eligibleCollection?.id || config.campaign.eligibleCollection.inherited) {
        issues.push({ level: 'error', code: 'campaign_collection_required', message: 'Campaign has a price offer but no own eligible collection. Saving claims are held until the approved SKU collection is set.' });
      } else {
        eligibilityDiagnostics(issues, 'campaign', config.campaign.eligibleCollection, true);
      }
    }
    if (config.evergreen?.enabled && sortTiers(config.evergreen?.tiers).length === 0) issues.push({ level: 'warning', code: 'evergreen_tiers_missing', message: 'Evergreen programme has no tier blocks.' });
    if (schedule.phase === 'invalid_start' || schedule.phase === 'invalid_end') issues.push({ level: 'error', code: 'campaign_schedule_invalid', message: 'Campaign start/end is not valid ISO 8601, or end is not after start.' });
    if (schedule.inWindow && config.master?.failClosed && !commercialConfirmed(config, 'campaign')) issues.push({ level: 'error', code: 'campaign_pricing_unconfirmed', message: 'Campaign is in schedule but pricing/reward is not confirmed. Live campaign surfaces are held.' });
    if (state.source === 'evergreen' && config.master?.failClosed && !commercialConfirmed(config, 'evergreen')) issues.push({ level: 'error', code: 'evergreen_pricing_unconfirmed', message: 'Evergreen pricing is not confirmed. Live promotional surfaces are held.' });
    if (config.campaign?.primaryOfferType === 'tiered' && sortTiers(config.campaign?.tiers).length === 0) issues.push({ level: 'error', code: 'campaign_tiers_missing', message: 'Campaign is set to tiered but has no campaign tier blocks.' });
    if (config.campaign?.primaryOfferType === 'bxgy' && !config.campaign?.bxgy?.rewardProductId && !config.campaign?.bxgy?.rewardCollectionId) issues.push({ level: 'error', code: 'bxgy_reward_missing', message: 'BXGY campaign has no reward product or reward collection.' });
    for (const gift of config.gifts || []) {
      if (!gift.productId) issues.push({ level: 'warning', code: `gift_product_missing_${gift.id}`, message: `Gift block ${gift.label || gift.id} has no product.` });
    }
    const identity = state.identity;
    if (identity?.phase === 'invalid_start' || identity?.phase === 'invalid_end') issues.push({ level: 'error', code: 'identity_schedule_invalid', message: 'Identity start/end is not valid ISO 8601, or end is not after start.' });
    if (identity?.reason === 'unpublished_theme_preview') issues.push({ level: 'info', code: 'identity_unpublished_preview', message: 'Identity is forced live because this theme is unpublished and the preview setting is on. Published themes ignore this setting.' });
    if (identity?.visible && identity.surfaces.navigation && !identity.hubUrl) issues.push({ level: 'warning', code: 'identity_hub_url_missing', message: 'Navigation identity is on but no campaign hub link is set.' });
    if (clockSource === 'client_unverified') issues.push({ level: 'warning', code: 'clock_unverified', message: 'Rendered server time looked stale and the clock sync failed; schedule uses the device clock. Shopify discounts remain the price authority.' });
    for (const [fragment, count] of Object.entries(config.__duplicates || {})) {
      issues.push({ level: 'error', code: `duplicate_${fragment}`, message: `Promotion Center has ${count} ${fragment} fragments. Keep exactly one.` });
    }
    return issues;
  };

  /* ---------- state ---------- */

  const resolveState = (config) => {
    if (!config) return null;
    const at = nowMs();
    const schedule = scheduleStatus(config.campaign, at);
    const editorPreview = config.designMode && config.master?.previewCampaignInEditor && config.campaign?.enabled;
    let source = 'off';
    let status = 'inactive';
    let reason = 'system_disabled';

    if (editorPreview) {
      source = 'campaign';
      status = 'active';
      reason = 'theme_editor_preview';
    } else if (config.master?.enabled) {
      if (schedule.inWindow) {
        source = 'campaign';
        const confirmed = commercialConfirmed(config, 'campaign');
        const eligibilityMissing = hasPriceOffer(config.campaign) && !eligibilityKnown(config.campaign?.eligibleCollection);
        if (eligibilityMissing) {
          status = 'held';
          reason = 'campaign_eligibility_unknown';
        } else {
          const held = config.master?.failClosed && !confirmed;
          status = held ? 'held' : 'active';
          reason = held ? 'campaign_pricing_unconfirmed' : 'campaign_active';
        }
      } else if (schedule.phase === 'ended' && config.campaign?.endBehavior === 'off') {
        source = 'off';
        status = 'inactive';
        reason = 'campaign_ended_off';
      } else if (config.evergreen?.enabled) {
        source = 'evergreen';
        const confirmed = commercialConfirmed(config, 'evergreen');
        const tiersNeedList = sortTiers(config.evergreen?.tiers).length > 0;
        const listIncomplete = tiersNeedList && config.evergreen?.eligibleCollection?.id && config.evergreen.eligibleCollection.complete !== true;
        if (config.master?.failClosed && !confirmed) {
          status = 'held';
          reason = 'evergreen_pricing_unconfirmed';
        } else if (config.master?.failClosed && listIncomplete) {
          status = 'held';
          reason = 'evergreen_eligibility_incomplete';
        } else {
          status = 'active';
          reason = 'evergreen_active';
        }
      } else {
        source = 'off';
        status = 'inactive';
        reason = schedule.phase === 'scheduled' ? 'campaign_not_started' : 'no_evergreen';
      }
    }

    const sourceConfig = source === 'campaign' ? config.campaign : config.evergreen;
    const state = {
      schemaVersion: config.schemaVersion,
      source,
      status,
      reason,
      schedule,
      publicName: source === 'campaign' ? config.campaign?.publicName : config.evergreen?.publicName,
      offer: source === 'campaign' ? campaignOffer(config.campaign) : evergreenOffer(config.evergreen),
      eligibleCollection: sourceConfig?.eligibleCollection || null,
      eligibilityKnown: source === 'campaign' ? eligibilityKnown(config.campaign?.eligibleCollection) : Boolean(config.evergreen?.eligibleCollection?.complete),
      shipping: effectiveShipping(config, source),
      gifts: source === 'off' ? [] : activeGifts(config, source),
      stacking: config.stacking || {},
      surfaces: source === 'campaign' ? config.campaign?.surfaces || {} : config.evergreen?.surfaces || {
        announcement: false,
        mixDock: true,
        pdp: true,
        cart: true,
        productBadges: false,
        homepageStrip: false,
      },
      copy: source === 'campaign' ? config.campaign?.copy || {} : {},
      countdown: source === 'campaign' ? config.campaign?.countdown || {} : { mode: 'off', label: '' },
      basketGoals: source === 'campaign' ? sortGoals(config.campaign?.basketGoals) : [],
      identity: resolveIdentity(config, at),
      clock: { source: clockSource, offsetMs: clockOffsetMs },
      mix: config.mix || {},
      shop: config.shop || {},
      diagnostics: [],
    };
    state.diagnostics = diagnosticsFor(config, state, schedule);
    return state;
  };

  /* ---------- DOM config ---------- */

  const parseJsonNode = (node) => {
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.error('[TEABALLS Promotion Center] Invalid JSON fragment.', node.dataset.teaballsPromotionFragment, error);
      return null;
    }
  };

  const parseConfigFromDom = () => {
    const nodes = [...document.querySelectorAll('script[data-teaballs-promotion-fragment]')];
    if (!nodes.length) return null;
    const fragments = {};
    const sectionIds = {};
    const duplicates = {};
    for (const node of nodes) {
      const type = node.dataset.teaballsPromotionFragment;
      if (!type) continue;
      if (fragments[type]) duplicates[type] = (duplicates[type] || 1) + 1;
      const parsed = parseJsonNode(node);
      if (parsed && !fragments[type]) {
        fragments[type] = parsed;
        sectionIds[type] = node.dataset.sectionId || '';
      }
    }
    const core = fragments.core;
    if (!core) {
      console.error('[TEABALLS Promotion Center] Core fragment is missing.');
      return null;
    }
    if (core.evergreen) core.evergreen.eligibleCollection = applyCompletedList('core', core.evergreen.eligibleCollection);
    const campaign = fragments.campaign || {};
    if (campaign.eligibleCollection?.id) {
      campaign.eligibleCollection = applyCompletedList('campaign', campaign.eligibleCollection);
    } else {
      const inheritedFrom = core.evergreen?.eligibleCollection;
      campaign.eligibleCollection = inheritedFrom ? { ...inheritedFrom, inherited: true } : null;
    }
    campaign.surfaces = { announcement: false, mixDock: false, pdp: false, cart: false, productBadges: false, homepageStrip: false };
    campaign.copy = campaign.copy || { headline: '', supporting: '', ctaLabel: '', ctaUrl: '', terms: '', announcement: '', mixDock: '', pdp: '', cart: '', badge: '' };
    for (const [key, value] of Object.entries(campaign)) {
      if (!key.startsWith('surface_') || !value?.surface) continue;
      campaign.surfaces[value.surface] = Boolean(value.enabled);
      if (value.copyOverride) campaign.copy[value.surface === 'productBadges' ? 'badge' : value.surface] = value.copyOverride;
      delete campaign[key];
    }
    return {
      ...core,
      campaign,
      identity: fragments.identity || null,
      gifts: fragments.gifts?.gifts || [],
      __sectionIds: sectionIds,
      __duplicates: duplicates,
      __fragments: Object.keys(fragments),
    };
  };

  const stateSignature = (state) => JSON.stringify({
    source: state?.source,
    status: state?.status,
    reason: state?.reason,
    publicName: state?.publicName,
    offer: state?.offer,
    eligibilityKnown: state?.eligibilityKnown,
    eligibleCount: state?.eligibleCollection?.productIds?.length || 0,
    shipping: state?.shipping,
    gifts: state?.gifts,
    surfaces: state?.surfaces,
    copy: state?.copy,
    schedule: state?.schedule?.phase,
    identity: state?.identity ? { key: state.identity.key, phase: state.identity.phase, surfaces: state.identity.surfaces } : null,
  });

  const dispatch = (type, detail) => {
    document.dispatchEvent(new CustomEvent(type, { detail }));
    for (const listener of listeners) {
      try { listener(detail); } catch (error) { console.error('[TEABALLS Promotion Center] Subscriber error.', error); }
    }
  };

  /* re-resolve from the current raw config; dispatch change only when the outcome changed */
  const evaluate = () => {
    if (!rawConfig) return resolvedState;
    const previousSignature = stateSignature(resolvedState);
    resolvedState = resolveState(rawConfig);
    applyIdentityToDocument(resolvedState.identity);
    if (previousSignature !== stateSignature(resolvedState)) dispatch(EVENT_CHANGE, resolvedState);
    scheduleTick();
    return resolvedState;
  };

  /* ---------- clock ---------- */

  const syncClock = async () => {
    if (clockSyncStarted || rawConfig?.designMode) return;
    clockSyncStarted = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const sentAt = Date.now();
      const response = await fetch(`${root}cart.js`, { method: 'HEAD', cache: 'no-store', credentials: 'same-origin' });
      const receivedAt = Date.now();
      const serverMs = parseDate(response.headers.get('Date'));
      if (serverMs === null) throw new Error('No Date header');
      clockOffsetMs = serverMs + 500 - Math.round((sentAt + receivedAt) / 2);
      clockSource = 'server_sync';
    } catch (error) {
      clockOffsetMs = 0;
      clockSource = 'client_unverified';
      console.warn('[TEABALLS Promotion Center] Clock sync failed; using device time.', error);
    }
    evaluate();
  };

  const adoptRenderedClock = (serverEpochMs) => {
    if (clockSource === 'server_sync') return;
    const offset = asNumber(serverEpochMs, Date.now()) - Date.now();
    if (Math.abs(offset) <= CLOCK_TRUST_MS) {
      clockOffsetMs = offset;
      clockSource = 'rendered';
      return;
    }
    /* rendered time is far from the device clock: cached HTML or a wrong device clock */
    clockOffsetMs = 0;
    clockSource = 'client_pending';
    syncClock();
  };

  /* ---------- eligibility completion ---------- */

  const completeList = async (fragment) => {
    const sectionId = rawConfig?.__sectionIds?.[fragment];
    const collection = fragment === 'core' ? rawConfig?.evergreen?.eligibleCollection : rawConfig?.campaign?.eligibleCollection;
    if (!sectionId || !listCanBeCompleted(collection) || collection.inherited) return;
    const key = listKey(fragment, collection);
    if (pendingLists.has(key) || completedLists.has(key)) return;
    pendingLists.add(key);
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}?sections=${encodeURIComponent(sectionId)}`, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
      const payload = await response.json();
      const html = payload?.[sectionId];
      if (!html) throw new Error('Section missing in response');
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const node = doc.querySelector(`script[data-teaballs-promotion-fragment="${fragment}"]`);
      const parsed = node ? JSON.parse(node.textContent || '{}') : null;
      const list = fragment === 'core' ? parsed?.evergreen?.eligibleCollection : parsed?.eligibleCollection;
      if (list?.id !== collection.id || list.complete !== true) throw new Error('Fetched list is not complete');
      completedLists.set(key, { productIds: list.productIds, productCount: list.productCount, listedCount: list.listedCount, listedPage: 1, complete: true });
      rawConfig = parseConfigFromDom() || rawConfig;
      if (rawConfig.__sectionIds) evaluate();
    } catch (error) {
      console.warn('[TEABALLS Promotion Center] Could not complete eligibility list; saving claims stay held.', fragment, error);
    } finally {
      pendingLists.delete(key);
    }
  };

  const completeListsIfNeeded = () => {
    if (!rawConfig) return;
    if (listCanBeCompleted(rawConfig.evergreen?.eligibleCollection)) completeList('core');
    if (listCanBeCompleted(rawConfig.campaign?.eligibleCollection) && !rawConfig.campaign.eligibleCollection.inherited) completeList('campaign');
  };

  /* ---------- refresh / ticking ---------- */

  const refresh = ({ forceReady = false } = {}) => {
    const previousSignature = stateSignature(resolvedState);
    const parsed = parseConfigFromDom();
    if (!parsed) return null;
    rawConfig = parsed;
    adoptRenderedClock(parsed.serverEpochMs);
    resolvedState = resolveState(rawConfig);
    applyIdentityToDocument(resolvedState.identity);
    const nextSignature = stateSignature(resolvedState);
    if (forceReady) dispatch(EVENT_READY, resolvedState);
    if (previousSignature && previousSignature !== nextSignature) dispatch(EVENT_CHANGE, resolvedState);
    completeListsIfNeeded();
    scheduleTick();
    return resolvedState;
  };

  const countdownWindowMs = (mode) => ({ last_48h: 48, last_24h: 24, last_12h: 12 }[mode] || 0) * 60 * 60 * 1000;

  const countdown = () => {
    const state = resolvedState;
    if (!state || state.source !== 'campaign' || state.status !== 'active') return { visible: false, remainingMs: 0, text: '' };
    const endMs = state.schedule?.endMs;
    if (!endMs) return { visible: false, remainingMs: 0, text: '' };
    const remainingMs = Math.max(0, endMs - nowMs());
    const mode = state.countdown?.mode || 'off';
    let visible = mode === 'always';
    const windowMs = countdownWindowMs(mode);
    if (windowMs) visible = remainingMs <= windowMs;
    if (mode === 'off' || remainingMs <= 0) visible = false;
    const totalSeconds = Math.floor(remainingMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const parts = [];
    if (days) parts.push(`${days}d`);
    parts.push(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
    return { visible, remainingMs, text: parts.join(' '), label: state.countdown?.label || '' };
  };

  /* next future moment at which the resolved outcome can change */
  const nextBoundaryMs = () => {
    if (!rawConfig) return null;
    const at = nowMs();
    const candidates = [];
    const campaign = rawConfig.campaign;
    if (campaign?.enabled) {
      const startMs = parseDate(campaign.startAt);
      const endMs = parseDate(campaign.endAt);
      candidates.push(startMs, endMs);
      const windowMs = countdownWindowMs(campaign.countdown?.mode);
      if (endMs !== null && windowMs) candidates.push(endMs - windowMs);
    }
    const identity = rawConfig.identity;
    if (identity?.enabled) candidates.push(parseDate(identity.startAt), parseDate(identity.endAt));
    const future = candidates.filter((value) => value !== null && value > at);
    return future.length ? Math.min(...future) : null;
  };

  const scheduleTick = () => {
    clearTimeout(tickTimer);
    tickTimer = 0;
    if (!resolvedState) return;
    const countdownVisible = countdown().visible;
    const boundary = nextBoundaryMs();
    if (!countdownVisible && boundary === null) return;
    let delay = countdownVisible ? 1000 : TICK_MAX_MS;
    if (boundary !== null) delay = Math.min(delay, boundary - nowMs() + 50);
    delay = Math.max(TICK_MIN_MS, delay);
    tickTimer = window.setTimeout(() => {
      evaluate();
      dispatch(EVENT_TICK, { state: resolvedState, countdown: countdown() });
    }, delay);
  };

  /* ---------- cart helpers (unchanged behaviour) ---------- */

  const tierProgress = (quantity, tiers = resolvedState?.offer?.tiers || []) => {
    const sorted = sortTiers(tiers);
    let current = null;
    let next = null;
    for (const tier of sorted) {
      if (quantity >= tier.minQuantity) current = tier;
      else { next = tier; break; }
    }
    return {
      current,
      next,
      remaining: next ? Math.max(0, next.minQuantity - quantity) : 0,
      maxReached: Boolean(sorted.length && current && current.id === sorted[sorted.length - 1].id),
    };
  };

  const goalProgress = (quantity, goals = resolvedState?.basketGoals || []) => {
    const sorted = sortGoals(goals);
    let current = null;
    let next = null;
    for (const goal of sorted) {
      if (quantity >= goal.quantity) current = goal;
      else { next = goal; break; }
    }
    return { current, next, remaining: next ? Math.max(0, next.quantity - quantity) : 0 };
  };

  const metricsFromCart = (cart) => {
    const state = resolvedState;
    const ids = collectionIds(state?.eligibleCollection);
    const items = Array.isArray(cart?.items) ? cart.items : [];
    let eligibleQuantity = 0;
    let eligibleSubtotalCents = 0;
    for (const item of items) {
      if (!ids.has(String(item.product_id))) continue;
      eligibleQuantity += asNumber(item.quantity, 0);
      eligibleSubtotalCents += asNumber(item.original_line_price ?? item.line_price ?? item.final_line_price, 0);
    }
    const cartSubtotalCents = asNumber(cart?.items_subtotal_price ?? cart?.original_total_price ?? cart?.total_price, 0);
    const metrics = { eligibleQuantity, eligibleSubtotalCents, cartSubtotalCents };
    const offer = state?.offer || {};
    let expectedDiscountCents = 0;
    let discountPercent = 0;
    if (offer.type === 'tiered') {
      const progress = tierProgress(eligibleQuantity, offer.tiers);
      discountPercent = asNumber(progress.current?.discountPercent, 0);
      expectedDiscountCents = Math.round(eligibleSubtotalCents * discountPercent / 100);
    } else if (offer.type === 'flat_percent' && thresholdMet(offer, metrics)) {
      discountPercent = asNumber(offer.flatPercent, 0);
      expectedDiscountCents = Math.round(eligibleSubtotalCents * discountPercent / 100);
    } else if (offer.type === 'fixed_amount' && thresholdMet(offer, metrics)) {
      expectedDiscountCents = Math.min(eligibleSubtotalCents, asNumber(offer.fixedAmountCents, 0));
    }
    const gifts = (state?.gifts || []).map((gift) => {
      const qualified = gift.triggerType === 'cart_subtotal'
        ? cartSubtotalCents >= asNumber(gift.thresholdSpendCents, 0)
        : eligibleQuantity >= asNumber(gift.thresholdQuantity, 0);
      const remaining = gift.triggerType === 'cart_subtotal'
        ? Math.max(0, asNumber(gift.thresholdSpendCents, 0) - cartSubtotalCents)
        : Math.max(0, asNumber(gift.thresholdQuantity, 0) - eligibleQuantity);
      return { ...gift, qualified, remaining };
    });
    const shippingRemainingCents = state?.shipping?.mode === 'threshold'
      ? Math.max(0, asNumber(state.shipping.thresholdCents, 0) - cartSubtotalCents)
      : 0;
    return {
      ...metrics,
      discountPercent,
      expectedDiscountCents,
      expectedEligibleTotalCents: Math.max(0, eligibleSubtotalCents - expectedDiscountCents),
      tier: tierProgress(eligibleQuantity),
      goal: goalProgress(eligibleQuantity),
      gifts,
      shippingRemainingCents,
    };
  };

  const formatMoney = (cents, currency = resolvedState?.shop?.currency || 'EUR', locale = resolvedState?.shop?.locale || 'de-DE') => {
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(asNumber(cents, 0) / 100);
    } catch {
      return `${(asNumber(cents, 0) / 100).toFixed(2)} ${currency}`;
    }
  };

  const surfaceEnabled = (name) => Boolean(resolvedState?.status === 'active' && resolvedState?.surfaces?.[name]);

  const identityEnabled = (name) => Boolean(resolvedState?.identity?.visible && resolvedState.identity.surfaces?.[name]);

  /* true only when an active, confirmed campaign offer covers this product (no claim when unknown) */
  const productEligible = (productId) => {
    const state = resolvedState;
    if (!state || state.source !== 'campaign' || state.status !== 'active' || !state.eligibilityKnown) return false;
    return collectionIds(state.eligibleCollection).has(String(productId));
  };

  const subscribe = (listener) => {
    if (typeof listener !== 'function') return () => {};
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const api = {
    version: '1.3.0',
    current: () => resolvedState,
    raw: () => rawConfig,
    refresh: () => refresh(),
    now: nowMs,
    countdown,
    tierProgress,
    goalProgress,
    metricsFromCart,
    formatMoney,
    surfaceEnabled,
    identityEnabled,
    productEligible,
    subscribe,
  };

  window.TeaballsPromotion = api;

  window[FLAG] = true;

  const init = () => refresh({ forceReady: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  /* background tabs throttle timers; re-check boundaries as soon as the page is visible again */
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') evaluate();
  });
  /* bfcache restore: the device→server offset is still valid, only the time moved on */
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) evaluate();
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target?.querySelector?.('script[data-teaballs-promotion-fragment]')) refresh();
  });
  document.addEventListener('shopify:section:unload', () => refresh());
})();
